import { Fragment, useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { useNavigate, useLocation, type NavigateFunction } from 'react-router-dom'
import {
  BookOpen,
  NotebookPen,
  CircleGauge,
  BookSearch,
  History,
  Telescope,
  Download,
  Workflow,
  Sun,
  Moon,
  Eclipse,
  ChevronRight,
  User,
  Settings,
  CreditCard,
  LogOut,
  Import,
  Keyboard,
  MessageCircleQuestion,
  Sparkles,
  GraduationCap,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAccountStore } from '@/stores/account-store'
import { trackEvent, EVENTS } from '@/services/analytics'
import { useTickerStore } from '@/stores/ticker-store'
import { useLastResearchTicker } from '@/features/research/hooks/use-recent-tickers'
import { useSettings } from '@/context/settings-hooks'
import { useTitleBarInset } from '@/services/platform/title-bar'
import { AppearanceDialog } from '@/features/theme-editor/components/appearance-dialog'
import { useNotificationStore } from '@/stores/notification-store'
import { showIntercom } from '@/services/intercom'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  buildLayoutImportPayload,
  buildPanelImportPayload,
  decodeShareLink,
  shareRoute,
  validateLayoutImport,
  validateProfileImport,
} from '@/workspace/layout-export'
import { ShortcutsDialog } from '@/features/shortcuts/shortcuts-dialog'
import { pageKeyForPath } from '@/features/shortcuts/shortcuts-catalog'
import { getWorkspaceMeta } from '@/workspace/idb-cache'
import { flattenLayouts } from '@/workspace/tree-state-manager'
import type { TreeItems } from '@/workspace/tree-state-manager'
import type { ProfileExport } from '@/types/schemas/workspace'
import type { LayoutSummary } from '@/workspace/types'
import { validateBacktestStrategyImport } from '@/features/backtest/utils/strategy-export'
import { useFeatureFlag } from '@/services/analytics/use-feature-flag'
import { FLAGS, type FlagKey } from '@/services/analytics/feature-flags'

/**
 * The rail follows the trader's workflow: the market now, then studying what
 * usually happens, then testing and review (Chris 2026-10-27).
 */
type NavGroup = 'market' | 'study' | 'review'

interface NavItem {
  label: string
  icon: React.ElementType
  to: string
  /** Rail group; a divider separates one group from the next. */
  group: NavGroup
  /** Rollout flag; the entry is hidden until it is on. The route is gated too. */
  flag?: FlagKey
  /** Released but still in beta: the entry carries a Beta tag. */
  beta?: boolean
}

// Tags deliberately has no primary-nav entry: the /tags manager is reached
// contextually ("Manage tags…" in the title-bar Tag dropdown and the history
// Tags-filter popover) — it's a supporting screen, not a workspace.
const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', icon: CircleGauge, to: '/dashboard', group: 'market' },
  { label: 'Research', icon: BookSearch, to: '/research', group: 'market' },
  { label: 'Playbook', icon: BookOpen, to: '/playbook', group: 'study', flag: FLAGS.PLAYBOOK },
  { label: 'History', icon: History, to: '/history', group: 'study' },
  {
    label: 'Deep Research',
    icon: Telescope,
    to: '/deep-research',
    group: 'study',
    flag: FLAGS.DEEP_RESEARCH,
    beta: true,
  },
  {
    label: 'Backtester',
    icon: Workflow,
    to: '/backtester',
    group: 'review',
    flag: FLAGS.BACKTESTER,
    beta: true,
  },
  { label: 'Journal', icon: NotebookPen, to: '/journal', group: 'review', flag: FLAGS.JOURNAL },
  { label: 'Exports', icon: Download, to: '/exports', group: 'review' },
]

const navBtnBase =
  'flex h-9 w-9 items-center justify-center rounded-md transition-colors text-sidebar-foreground/60 hover:bg-sidebar-foreground/10 hover:text-sidebar-foreground'

/** The Beta tag: a pill at the end of an expanded row, a notch under a rail icon. */
const betaTagBase =
  'rounded-sm bg-primary font-semibold uppercase tracking-wide text-primary-foreground'

const navRowBase =
  'flex h-10 w-full items-center gap-3 rounded-md px-3 text-sm transition-colors text-sidebar-foreground/70 hover:bg-sidebar-foreground/10 hover:text-sidebar-foreground'

// ─── Import Link Modal ───

type ImportStep = 'paste' | 'pick-layout'

interface DecodeShareLinkDeps {
  linkValue: string
  onClose: () => void
  navigate: NavigateFunction
  setImporting: (importing: boolean) => void
  setPendingProfile: (profile: ProfileExport | null) => void
  setLayouts: (layouts: LayoutSummary[]) => void
  setSelectedLayoutId: (id: string | null) => void
  setStep: (step: ImportStep) => void
}

async function decodeAndRouteShareLink({
  linkValue,
  onClose,
  navigate,
  setImporting,
  setPendingProfile,
  setLayouts,
  setSelectedLayoutId,
  setStep,
}: DecodeShareLinkDeps): Promise<void> {
  if (!linkValue.trim()) return
  setImporting(true)
  try {
    if (!linkValue.includes('#')) {
      useNotificationStore.getState().showError('Invalid share link — no data found')
      setImporting(false)
      return
    }
    const decoded = await decodeShareLink(linkValue)

    // ─── Layout import: auto-add ───
    const layoutData = validateLayoutImport(decoded)
    if (layoutData) {
      sessionStorage.setItem(
        'edge-pending-import',
        JSON.stringify(buildLayoutImportPayload(layoutData)),
      )
      onClose()
      navigate(shareRoute(layoutData.scope))
      setTimeout(() => window.dispatchEvent(new Event('edge:pending-import')), 0)
      return
    }

    // ─── Panel/profile import: show layout picker ───
    const profileData = validateProfileImport(decoded)
    if (profileData) {
      // Read available layouts from IDB (derive flat list from tree)
      const meta = await getWorkspaceMeta(profileData.scope)
      const layoutList: LayoutSummary[] = meta?.treeItems
        ? flattenLayouts(meta.treeItems as TreeItems)
        : []

      if (layoutList.length === 0) {
        // IDB may simply be cold. Let the scoped workspace consume this on
        // its first unlocked layout; if none exists, it remains pending.
        storePanelImport(profileData, '')
        onClose()
        navigate(shareRoute(profileData.scope))
        setTimeout(() => window.dispatchEvent(new Event('edge:pending-panel-import')), 0)
        return
      }

      if (layoutList.length === 1) {
        // Only one layout — auto-select
        storePanelImport(profileData, layoutList[0].layoutId)
        onClose()
        navigate(shareRoute(profileData.scope))
        setTimeout(() => window.dispatchEvent(new Event('edge:pending-panel-import')), 0)
        return
      }

      // Multiple layouts — show picker
      setPendingProfile(profileData)
      setLayouts(layoutList)
      setSelectedLayoutId(
        layoutList.some((layout) => layout.layoutId === meta?.activeLayoutId)
          ? (meta?.activeLayoutId ?? null)
          : layoutList[0].layoutId,
      )
      setStep('pick-layout')
      return
    }

    if (validateBacktestStrategyImport(decoded)) {
      const fragment = linkValue.slice(linkValue.indexOf('#'))
      onClose()
      navigate(`/import${fragment}`)
      return
    }

    useNotificationStore.getState().showError('Could not recognize the shared data format')
  } catch {
    useNotificationStore.getState().showError('Failed to decode share link')
  } finally {
    setImporting(false)
  }
}

function ImportLinkModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [linkValue, setLinkValue] = useState('')
  const [importing, setImporting] = useState(false)
  const [step, setStep] = useState<ImportStep>('paste')
  const [pendingProfile, setPendingProfile] = useState<ProfileExport | null>(null)
  const [layouts, setLayouts] = useState<LayoutSummary[]>([])
  const [selectedLayoutId, setSelectedLayoutId] = useState<string | null>(null)
  const navigate = useNavigate()

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!open) {
      setLinkValue('')
      setImporting(false)
      setStep('paste')
      setPendingProfile(null)
      setLayouts([])
      setSelectedLayoutId(null)
    }
  }, [open])

  const handleDecode = () =>
    decodeAndRouteShareLink({
      linkValue,
      onClose,
      navigate,
      setImporting,
      setPendingProfile,
      setLayouts,
      setSelectedLayoutId,
      setStep,
    })

  const handleConfirmLayout = () => {
    if (!pendingProfile || !selectedLayoutId) return
    storePanelImport(pendingProfile, selectedLayoutId)
    onClose()
    navigate(shareRoute(pendingProfile.scope))
    setTimeout(() => window.dispatchEvent(new Event('edge:pending-panel-import')), 0)
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-500 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg border border-border bg-popover p-4 shadow-lg">
        {step === 'paste' && (
          <>
            <h3 className="mb-2 text-sm font-medium text-foreground">Import shared link</h3>
            <p className="mb-3 text-xs text-muted-foreground">
              Paste a shared layout, panel, or strategy link to import it.
            </p>
            <input
              value={linkValue}
              onChange={(e) => setLinkValue(e.target.value)}
              placeholder="Paste share link here..."
              className="w-full rounded border border-border bg-background px-2 py-1.5 text-xs text-foreground outline-hidden placeholder:text-muted-foreground/50"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && linkValue.trim()) handleDecode()
                if (e.key === 'Escape') onClose()
              }}
            />
            <div className="mt-3 flex justify-end gap-2">
              <button
                onClick={onClose}
                className="rounded px-3 py-1 text-xs text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
              <Button size="sm" onClick={handleDecode} disabled={!linkValue.trim() || importing}>
                {importing ? 'Importing...' : 'Import'}
              </Button>
            </div>
          </>
        )}

        {step === 'pick-layout' && pendingProfile && (
          <>
            <h3 className="mb-2 text-sm font-medium text-foreground">Add Panel to Layout</h3>
            <p className="mb-3 text-xs text-muted-foreground">
              Importing <span className="font-medium text-foreground">{pendingProfile.name}</span> (
              {pendingProfile.profileType}). Which layout should it be added to?
            </p>
            <Select value={selectedLayoutId ?? undefined} onValueChange={setSelectedLayoutId}>
              <SelectTrigger aria-label="Destination layout" className="h-8 bg-background text-xs">
                <SelectValue placeholder="Select a layout" />
              </SelectTrigger>
              <SelectContent className="z-510">
                {layouts.map((l) => (
                  <SelectItem key={l.layoutId} value={l.layoutId} className="text-xs">
                    {l.name || 'Default'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="mt-3 flex justify-end gap-2">
              <button
                onClick={() => {
                  setStep('paste')
                  setPendingProfile(null)
                }}
                className="rounded px-3 py-1 text-xs text-muted-foreground hover:text-foreground"
              >
                Back
              </button>
              <Button size="sm" onClick={handleConfirmLayout} disabled={!selectedLayoutId}>
                Add Panel
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function storePanelImport(profile: ProfileExport, targetLayoutId: string) {
  sessionStorage.setItem(
    'edge-pending-panel-import',
    JSON.stringify(buildPanelImportPayload(profile, targetLayoutId)),
  )
}

// ─── Nav Sidebar ───

interface NavSidebarProps {
  /** Mobile / drawer mode — render full-width rows with labels instead of icon-only buttons. */
  expanded?: boolean
  /** Called after a navigation occurs (e.g. to close the mobile drawer). */
  onNavigate?: () => void
}

export function NavSidebar({ expanded = false, onNavigate }: NavSidebarProps = {}) {
  const navigate = useNavigate()
  const location = useLocation()
  const globalTicker = useTickerStore((s) => s.symbol)
  const researchTicker = useLastResearchTicker() ?? globalTicker
  const { theme } = useSettings()
  // Desktop app: the macOS window buttons sit where the collapsed rail's mark is.
  const titleBarInset = useTitleBarInset()
  const [importOpen, setImportOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const [appearanceOpen, setAppearanceOpen] = useState(false)
  const [tooltipBlocked, setTooltipBlocked] = useState(false)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)

  const handleProfileOpenChange = (open: boolean) => {
    setProfileOpen(open)
    if (!open) {
      setTooltipBlocked(true)
      setTimeout(() => setTooltipBlocked(false), 300)
    }
  }

  const handleLogout = async () => {
    trackEvent(EVENTS.USER_LOGGED_OUT, {})
    await useAccountStore.getState().logout()
    navigate('/login', { replace: true })
  }

  const getTo = (item: NavItem) =>
    item.to === '/research' ? `/research/${researchTicker}` : item.to
  // Hooks cannot run inside the map, so each flagged surface reads its
  // flag here and the list is filtered once.
  const backtesterOn = useFeatureFlag(FLAGS.BACKTESTER)
  const deepResearchOn = useFeatureFlag(FLAGS.DEEP_RESEARCH)
  const playbookOn = useFeatureFlag(FLAGS.PLAYBOOK)
  const journalOn = useFeatureFlag(FLAGS.JOURNAL)
  const flagOn: Record<FlagKey, boolean> = {
    [FLAGS.BACKTESTER]: backtesterOn,
    [FLAGS.DEEP_RESEARCH]: deepResearchOn,
    [FLAGS.PLAYBOOK]: playbookOn,
    [FLAGS.JOURNAL]: journalOn,
    // Notebook has no nav entry; its flag gates the dashboard Notebook panel
    // (workspace/panel-rollout.ts) and the research Notes surfaces.
    [FLAGS.NOTEBOOK]: true,
    // Home is a dashboard tab, gated in dashboard-page / dashboard-header.
    [FLAGS.HOME]: true,
    // Similar Patterns has no nav entry; it is gated inside History and the chart menu.
    [FLAGS.SIMILAR_PATTERNS]: true,
    // Learn to Trade lives inside University (always listed); it is gated in learn-page.
    [FLAGS.LEARN_TO_TRADE]: true,
    // EdgeScript has no nav entry; it is gated on the chart, Build and the companion.
    [FLAGS.EDGESCRIPT]: true,
    // Chart drawing has no nav entry; it gates the companion's chart tools.
    [FLAGS.AI_CHART_DRAWING]: true,
    // Scanner minute snapshot has no nav entry; it is gated in the scanner header and panel.
    [FLAGS.SCANNER_MINUTE_SNAPSHOT]: true,
    // The run report's Robustness and Sizing pages are gated in run-workspace-page.
    [FLAGS.BACKTEST_SIZING_ROBUSTNESS]: true,
    // Price Alerts / Alert Windows have no nav entry; gated by panel-rollout and the notifiers.
    [FLAGS.PRICE_ALERTS]: true,
    [FLAGS.ALERT_WINDOWS]: true,
  }
  const navItems = NAV_ITEMS.filter((item) => !item.flag || flagOn[item.flag])
  // A divider opens each group after the first, just before its first visible
  // entry, so a flagged-off entry never leaves a stray divider behind.
  const opensGroup = (i: number) => i > 0 && navItems[i].group !== navItems[i - 1].group

  const isActive = (to: string) => location.pathname.startsWith(to)

  const goTo = (to: string) => {
    navigate(to)
    onNavigate?.()
  }

  if (expanded) {
    return (
      <>
        <div className="flex h-full w-full flex-col text-sidebar-foreground">
          {/* Logo — clicking returns to the dashboard */}
          <div className="flex h-12 items-center px-4">
            <button
              type="button"
              onClick={() => goTo('/dashboard')}
              aria-label="Go to dashboard"
              className="flex items-center rounded outline-none focus-visible:ring-2 focus-visible:ring-sidebar-foreground/40"
            >
              <img
                src="/static/EdgeToTradeWhiteText.svg"
                alt="Edge to Trade"
                className={cn('h-6 w-auto', theme === 'light' && 'invert-[.82]')}
              />
            </button>
          </div>

          {/* Main nav */}
          <nav className="flex flex-1 flex-col gap-0.5 px-2 pt-2">
            {navItems.map((item, i) => {
              const Icon = item.icon
              const to = getTo(item)
              const active = isActive(item.to)
              return (
                <Fragment key={item.label}>
                  {opensGroup(i) && (
                    <Separator
                      data-testid="nav-group-divider"
                      className="my-1 bg-sidebar-foreground/15"
                    />
                  )}
                  <button
                    data-companion-label={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => goTo(to)}
                    className={cn(navRowBase, active && 'text-sidebar-foreground')}
                    style={active ? { backgroundColor: 'hsl(var(--accent))' } : undefined}
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                    <span>{item.label}</span>
                    {item.beta && (
                      <span
                        className={cn(
                          betaTagBase,
                          'ml-auto px-1.5 py-0.5 text-[10px] leading-none',
                        )}
                      >
                        Beta
                      </span>
                    )}
                  </button>
                </Fragment>
              )
            })}
          </nav>

          {/* Bottom utilities */}
          <div className="flex flex-col gap-0.5 px-2 pb-3">
            <button onClick={showIntercom} className={navRowBase}>
              <MessageCircleQuestion className="h-5 w-5 shrink-0" />
              <span>Help</span>
            </button>
            <button onClick={() => goTo('/account')} className={navRowBase}>
              <Settings className="h-5 w-5 shrink-0" />
              <span>Account</span>
            </button>
            <button onClick={() => goTo('/account/skills')} className={navRowBase}>
              <Sparkles className="h-5 w-5 shrink-0" />
              <span>Skills</span>
            </button>
            <button
              onClick={() => {
                setImportOpen(true)
                onNavigate?.()
              }}
              className={navRowBase}
            >
              <Import className="h-5 w-5 shrink-0" />
              <span>Import shared link</span>
            </button>
            <button onClick={() => goTo('/account/billing')} className={navRowBase}>
              <CreditCard className="h-5 w-5 shrink-0" />
              <span>Billing</span>
            </button>
            <button onClick={() => setAppearanceOpen(true)} className={navRowBase}>
              {theme === 'light' && <Sun className="h-5 w-5 shrink-0" />}
              {theme === 'dark' && <Moon className="h-5 w-5 shrink-0" />}
              {theme === 'black' && <Eclipse className="h-5 w-5 shrink-0" />}
              <span className="flex-1 text-left">Appearance</span>
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
            <Separator className="my-1" />
            <button
              onClick={handleLogout}
              className={cn(navRowBase, 'text-destructive hover:bg-destructive/10')}
            >
              <LogOut className="h-5 w-5 shrink-0" />
              <span>Logout</span>
            </button>
          </div>
        </div>

        <ImportLinkModal open={importOpen} onClose={() => setImportOpen(false)} />
        <AppearanceDialog open={appearanceOpen} onOpenChange={setAppearanceOpen} />
      </>
    )
  }

  return (
    <>
      <TooltipProvider>
        <div className="flex h-full w-full flex-col text-sidebar-foreground">
          {titleBarInset ? (
            // The window buttons fill this slot; the space around them moves the window.
            <div data-window-drag className="h-10 shrink-0" />
          ) : (
            // Logo: clicking returns to the dashboard
            <div className="flex h-10 items-center justify-center">
              <button
                type="button"
                onClick={() => goTo('/dashboard')}
                aria-label="Go to dashboard"
                className="flex items-center rounded outline-none focus-visible:ring-2 focus-visible:ring-sidebar-foreground/40"
              >
                <img
                  src="/static/EdgeToTradeWhiteText-e.svg"
                  alt="Edge"
                  className={cn('h-6 w-auto', theme === 'light' && 'invert-[.82]')}
                />
              </button>
            </div>
          )}

          {/* Main nav */}
          <nav
            className="flex flex-1 flex-col items-center"
            style={{ gap: '0.65rem', paddingTop: '0.45rem' }}
          >
            {navItems.map((item, i) => {
              const Icon = item.icon
              const to = getTo(item)
              const active = isActive(item.to)
              return (
                <Fragment key={item.label}>
                  {opensGroup(i) && (
                    <Separator
                      data-testid="nav-group-divider"
                      className="w-6 bg-sidebar-foreground/15"
                    />
                  )}
                  <Tooltip delayDuration={200}>
                    <TooltipTrigger asChild>
                      <button
                        data-companion-label={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        aria-label={item.beta ? `${item.label} (Beta)` : item.label}
                        onClick={() => navigate(to)}
                        className={cn(navBtnBase, 'relative', active && 'text-sidebar-foreground')}
                        style={active ? { backgroundColor: 'hsl(var(--accent))' } : undefined}
                      >
                        <Icon className="h-5 w-5" />
                        {item.beta && (
                          <span
                            aria-hidden
                            className={cn(
                              betaTagBase,
                              'pointer-events-none absolute -bottom-1 left-1/2 -translate-x-1/2 px-[3px] text-[7px] leading-[10px]',
                            )}
                          >
                            Beta
                          </span>
                        )}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      {item.beta ? `${item.label} (Beta)` : item.label}
                    </TooltipContent>
                  </Tooltip>
                </Fragment>
              )
            })}
          </nav>

          {/* Help & Profile */}
          <div className="flex flex-col items-center pb-4" style={{ gap: '0.65rem' }}>
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <button onClick={() => navigate('/university')} className={navBtnBase}>
                  <GraduationCap className="h-5 w-5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">University</TooltipContent>
            </Tooltip>

            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <button onClick={showIntercom} className={navBtnBase}>
                  <MessageCircleQuestion className="h-5 w-5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">Help</TooltipContent>
            </Tooltip>

            <Popover open={profileOpen} onOpenChange={handleProfileOpenChange}>
              <Tooltip delayDuration={200} open={profileOpen || tooltipBlocked ? false : undefined}>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <button className={navBtnBase}>
                      <User className="h-5 w-5" />
                    </button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent side="right">Profile</TooltipContent>
              </Tooltip>

              <PopoverContent side="right" align="end" className="w-48 p-1" sideOffset={8}>
                <button
                  onClick={() => {
                    handleProfileOpenChange(false)
                    navigate('/account')
                  }}
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-hover hover:text-accent-foreground"
                >
                  <Settings className="h-4 w-4" />
                  Account
                </button>
                <button
                  onClick={() => {
                    handleProfileOpenChange(false)
                    navigate('/account/skills')
                  }}
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-hover hover:text-accent-foreground"
                >
                  <Sparkles className="h-4 w-4" />
                  Skills
                </button>
                <button
                  onClick={() => {
                    handleProfileOpenChange(false)
                    setImportOpen(true)
                  }}
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-hover hover:text-accent-foreground"
                >
                  <Import className="h-4 w-4" />
                  Import shared link
                </button>
                <button
                  onClick={() => {
                    handleProfileOpenChange(false)
                    navigate('/account/billing')
                  }}
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-hover hover:text-accent-foreground"
                >
                  <CreditCard className="h-4 w-4" />
                  Billing
                </button>
                <button
                  onClick={() => {
                    handleProfileOpenChange(false)
                    setAppearanceOpen(true)
                  }}
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-hover hover:text-accent-foreground"
                >
                  {theme === 'light' && <Sun className="h-4 w-4" />}
                  {theme === 'dark' && <Moon className="h-4 w-4" />}
                  {theme === 'black' && <Eclipse className="h-4 w-4" />}
                  <span className="flex-1 text-left">Appearance</span>
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
                <button
                  onClick={() => {
                    handleProfileOpenChange(false)
                    setShortcutsOpen(true)
                  }}
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-hover hover:text-accent-foreground"
                >
                  <Keyboard className="h-4 w-4" />
                  Keyboard shortcuts
                </button>
                <Separator className="my-1" />
                <button
                  onClick={handleLogout}
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-destructive hover:bg-destructive/10"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </button>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </TooltipProvider>

      <ImportLinkModal open={importOpen} onClose={() => setImportOpen(false)} />
      <AppearanceDialog open={appearanceOpen} onOpenChange={setAppearanceOpen} />
      <ShortcutsDialog
        open={shortcutsOpen}
        onOpenChange={setShortcutsOpen}
        defaultPageKey={pageKeyForPath(location.pathname)}
      />
    </>
  )
}
